import React, { useState, useEffect } from 'react';
import { Building2, Users, Plus, X, RefreshCw, AlertCircle, Shield, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BILLING_URL = process.env.REACT_APP_BILLING_URL || '';

const getAuthHeader = () => {
  try {
    const user = JSON.parse(localStorage.getItem('juridic_user') || '{}');
    return user.token ? `Bearer ${user.token}` : null;
  } catch {
    return null;
  }
};

const authFetch = (url, opts = {}) => {
  const auth = getAuthHeader();
  return fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? { 'Authorization': auth } : {}),
      ...(opts.headers || {}),
    },
  });
};

export default function Gestao() {
  const { darkMode } = useAuth();
  const [tab, setTab] = useState('companies');

  const [companies, setCompanies] = useState([]);
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState(null);
  const [showCompForm, setShowCompForm] = useState(false);
  const [newComp, setNewComp] = useState({ company: '', role: 'user' });
  const [compSaving, setCompSaving] = useState(false);
  const [deletingCompId, setDeletingCompId] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [newUser, setNewUser] = useState({ user: '', password: '', company_id: '' });
  const [userSaving, setUserSaving] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);

  const loadCompanies = async () => {
    setCompLoading(true);
    setCompError(null);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/companies`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setCompanies(await res.json());
    } catch (err) {
      setCompError(err.message);
    } finally {
      setCompLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/users`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setUsers(await res.json());
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    loadUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    setCompSaving(true);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/companies`, {
        method: 'POST',
        body: JSON.stringify(newComp),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setNewComp({ company: '', role: 'user' });
      setShowCompForm(false);
      await loadCompanies();
    } catch (err) {
      setCompError(err.message);
    } finally {
      setCompSaving(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserSaving(true);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/users`, {
        method: 'POST',
        body: JSON.stringify(newUser),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setNewUser({ user: '', password: '', company_id: '' });
      setShowUserForm(false);
      await loadUsers();
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUserSaving(false);
    }
  };

  const handleDeleteCompany = async (id) => {
    setDeletingCompId(id);
    setCompError(null);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/companies/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      await loadCompanies();
    } catch (err) {
      setCompError(err.message);
    } finally {
      setDeletingCompId(null);
    }
  };

  const handleDeleteUser = async (id) => {
    setDeletingUserId(id);
    setUsersError(null);
    try {
      const res = await authFetch(`${BILLING_URL}/admin/users/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      await loadUsers();
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/30 ${darkMode ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`;
  const labelCls = `block text-xs font-medium mb-1 ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`;
  const cardCls = `rounded-xl border ${darkMode ? 'bg-neutral-900 border-neutral-800' : 'bg-white border-gray-200'}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl font-bold flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          <Shield className="w-8 h-8" />
          Gestão
        </h2>
        <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
          Administração de empresas e usuários
        </p>
      </div>

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl w-fit ${darkMode ? 'bg-neutral-800' : 'bg-gray-100'}`}>
        {[
          { key: 'companies', label: 'Empresas', icon: <Building2 className="w-4 h-4" /> },
          { key: 'users', label: 'Usuários', icon: <Users className="w-4 h-4" /> },
        ].map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              tab === key
                ? (darkMode ? 'bg-neutral-700 text-white shadow' : 'bg-white text-gray-900 shadow')
                : (darkMode ? 'text-neutral-400 hover:text-neutral-200' : 'text-gray-500 hover:text-gray-700')
            }`}
          >
            {icon}{label}
          </button>
        ))}
      </div>

      {/* ── Companies Tab ── */}
      {tab === 'companies' && (
        <div className={`${cardCls} overflow-hidden`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
            <p className={`text-sm font-semibold ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
              Empresas ({companies.length})
            </p>
            <div className="flex items-center gap-2">
              <button onClick={loadCompanies} disabled={compLoading} className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-neutral-400 hover:bg-neutral-800' : 'text-gray-500 hover:bg-gray-100'}`}>
                <RefreshCw className={`w-4 h-4 ${compLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowCompForm(!showCompForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-all"
              >
                {showCompForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showCompForm ? 'Cancelar' : 'Nova Empresa'}
              </button>
            </div>
          </div>

          {compError && (
            <div className={`flex items-center gap-2 mx-5 mt-4 p-3 rounded-lg border text-sm ${darkMode ? 'bg-red-900/20 border-red-800/50 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{compError}
            </div>
          )}

          {showCompForm && (
            <form onSubmit={handleCreateCompany} className={`mx-5 my-4 p-4 rounded-xl border ${darkMode ? 'bg-neutral-800/50 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-sm font-semibold mb-4 ${darkMode ? 'text-neutral-200' : 'text-gray-700'}`}>Nova Empresa</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Nome da empresa</label>
                  <input
                    required
                    className={inputCls}
                    placeholder="Ex: Empresa ABC"
                    value={newComp.company}
                    onChange={e => setNewComp(p => ({ ...p, company: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Perfil</label>
                  <select
                    required
                    className={inputCls}
                    value={newComp.role}
                    onChange={e => setNewComp(p => ({ ...p, role: e.target.value }))}
                  >
                    <option value="user">Usuário</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button type="submit" disabled={compSaving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50 transition-all">
                  {compSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Criar Empresa
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  {['Empresa', 'Perfil', 'ID', ''].map(h => (
                    <th key={h} className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.length === 0 && !compLoading ? (
                  <tr>
                    <td colSpan={3} className={`px-5 py-10 text-center text-sm ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                      Nenhuma empresa cadastrada
                    </td>
                  </tr>
                ) : companies.map(c => (
                  <tr key={c._id} className={`border-b last:border-0 ${darkMode ? 'border-neutral-800 hover:bg-neutral-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className={`px-5 py-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.company}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        c.role === 'admin'
                          ? (darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700')
                          : (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                      }`}>
                        {c.role}
                      </span>
                    </td>
                    <td className={`px-5 py-3 font-mono text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>{c._id}</td>
                    <td className="px-3 py-3 text-right">
                      {c.role !== 'admin' && (
                        <button
                          onClick={() => handleDeleteCompany(c._id)}
                          disabled={deletingCompId === c._id}
                          title="Excluir empresa"
                          className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'} disabled:opacity-40`}
                        >
                          {deletingCompId === c._id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Users Tab ── */}
      {tab === 'users' && (
        <div className={`${cardCls} overflow-hidden`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
            <p className={`text-sm font-semibold ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
              Usuários ({users.length})
            </p>
            <div className="flex items-center gap-2">
              <button onClick={loadUsers} disabled={usersLoading} className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-neutral-400 hover:bg-neutral-800' : 'text-gray-500 hover:bg-gray-100'}`}>
                <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowUserForm(!showUserForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-all"
              >
                {showUserForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showUserForm ? 'Cancelar' : 'Novo Usuário'}
              </button>
            </div>
          </div>

          {usersError && (
            <div className={`flex items-center gap-2 mx-5 mt-4 p-3 rounded-lg border text-sm ${darkMode ? 'bg-red-900/20 border-red-800/50 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{usersError}
            </div>
          )}

          {showUserForm && (
            <form onSubmit={handleCreateUser} className={`mx-5 my-4 p-4 rounded-xl border ${darkMode ? 'bg-neutral-800/50 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-sm font-semibold mb-4 ${darkMode ? 'text-neutral-200' : 'text-gray-700'}`}>Novo Usuário</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Usuário</label>
                  <input
                    required
                    className={inputCls}
                    placeholder="user_exemplo"
                    value={newUser.user}
                    onChange={e => setNewUser(p => ({ ...p, user: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Senha</label>
                  <input
                    required
                    type="password"
                    className={inputCls}
                    placeholder="Senha"
                    value={newUser.password}
                    onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Empresa</label>
                  <select
                    required
                    className={inputCls}
                    value={newUser.company_id}
                    onChange={e => setNewUser(p => ({ ...p, company_id: e.target.value }))}
                  >
                    <option value="">Selecionar...</option>
                    {companies.map(c => (
                      <option key={c._id} value={c._id}>{c.company} ({c.role})</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <button type="submit" disabled={userSaving} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50 transition-all">
                  {userSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Criar Usuário
                </button>
              </div>
            </form>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
                  {['Usuário', 'Empresa', 'Perfil', 'ID', ''].map(h => (
                    <th key={h} className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && !usersLoading ? (
                  <tr>
                    <td colSpan={4} className={`px-5 py-10 text-center text-sm ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>
                      Nenhum usuário cadastrado
                    </td>
                  </tr>
                ) : users.map(u => (
                  <tr key={u._id} className={`border-b last:border-0 ${darkMode ? 'border-neutral-800 hover:bg-neutral-800/40' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className={`px-5 py-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{u.user}</td>
                    <td className={`px-5 py-3 ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>{u.companyData?.company || '-'}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        u.companyData?.role === 'admin'
                          ? (darkMode ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-100 text-amber-700')
                          : (darkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                      }`}>
                        {u.companyData?.role || '-'}
                      </span>
                    </td>
                    <td className={`px-5 py-3 font-mono text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>{u._id}</td>
                    <td className="px-3 py-3 text-right">
                      {u.companyData?.role !== 'admin' && (
                        <button
                          onClick={() => handleDeleteUser(u._id)}
                          disabled={deletingUserId === u._id}
                          title="Excluir usuário"
                          className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'} disabled:opacity-40`}
                        >
                          {deletingUserId === u._id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <Trash2 className="w-4 h-4" />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
